import { faPlay, faPause, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Howl } from 'howler';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAiAssistant } from '../../AiAssistantContext';
import * as api from '../../api';
import { ImageViewerButtons, useImageViewer } from './ImageViewerControls';

// --- Audio Player ---

interface IAudioProps {
  src: string;
}

export const InlineAudioPlayer: React.FC<IAudioProps> = ({ src }) => {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);
  const filename = src.split('/').pop() || src;

  const sound = useMemo(
    () =>
      new Howl({
        src: [src],
        preload: false,
      }),
    [src],
  );

  useEffect(() => {
    sound.on('end', () => setPlaying(false));
    sound.on('loaderror', () => { setPlaying(false); setError(true); });
    sound.on('playerror', () => { setPlaying(false); setError(true); });
    return () => {
      sound.unload();
    };
  }, [sound]);

  const toggle = useCallback(() => {
    if (error) return;
    if (playing) {
      sound.stop();
      setPlaying(false);
    } else {
      sound.load();
      sound.play();
      setPlaying(true);
    }
  }, [playing, error, sound]);

  return (
    <AudioRow onClick={toggle} $error={error}>
      <AudioIcon>
        <FontAwesomeIcon icon={error ? faExclamationTriangle : playing ? faPause : faPlay} />
      </AudioIcon>
      <AudioName>{filename}</AudioName>
    </AudioRow>
  );
};

const AudioRow = styled.span<{ $error: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 8px;
  background: #e8f0fe;
  border-radius: 4px;
  cursor: ${(p) => (p.$error ? 'default' : 'pointer')};
  font-size: 13px;
  color: #1967d2;
  &:hover {
    background: ${(p) => (p.$error ? '#fce8e6' : '#d2e3fc')};
  }
`;

const AudioIcon = styled.span`
  font-size: 11px;
  width: 14px;
  text-align: center;
`;

const AudioName = styled.span`
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 12px;
`;

// --- Image Viewer ---

interface IImageProps {
  src: string;
  alt?: string;
}

export const InlineImage: React.FC<IImageProps> = ({ src, alt }) => {
  const [errored, setErrored] = useState(false);
  const [v] = useState(() => Date.now());
  const bustedSrc = `${src}?v=${v}`;
  const filename = src.split('/').pop() || 'image.png';
  const { bgColor, isHovering, hoverProps, toggleDarkMode, handleDownload } = useImageViewer(bustedSrc, filename);

  if (errored) {
    return <ImageError>Image not found</ImageError>;
  }

  return (
    <ImageWrap $bgColor={bgColor} {...hoverProps}>
      <StyledImg
        src={bustedSrc}
        alt={alt || ''}
        onError={() => setErrored(true)}
        onClick={() => window.open(bustedSrc, '_blank')}
      />
      {isHovering && <ImageViewerButtons onDownload={handleDownload} onToggleDark={toggleDarkMode} />}
    </ImageWrap>
  );
};

// --- Project Image (fetched via AI assistant files API) ---

interface IProjectImageProps {
  src: string;
  alt?: string;
}

export const InlineProjectImage: React.FC<IProjectImageProps> = ({ src, alt }) => {
  const { state, dispatch } = useAiAssistant();

  const match = src.match(/\/ai-assistant\/projects\/(\d+)\/files\/(.+)/);
  const projectId = match ? Number(match[1]) : null;
  const filePath = match ? decodeURIComponent(match[2]) : null;

  const cached = filePath ? state.fileContents[filePath] : undefined;

  const [dataSrc, setDataSrc] = useState<string | null>(cached ?? null);
  const [errored, setErrored] = useState(false);

  const filename = filePath?.split('/').pop() || 'image.png';
  const { bgColor, isHovering, hoverProps, toggleDarkMode, handleDownload } = useImageViewer(dataSrc, filename);

  useEffect(() => {
    if (cached !== undefined) {
      setDataSrc(cached);
      return;
    }
    if (!projectId || !filePath) { setErrored(true); return; }
    api.fetchFileContent(projectId, filePath)
      .then((res) => {
        dispatch({ type: 'SET_FILE_CONTENT', path: filePath, content: res.content });
        setDataSrc(res.content);
      })
      .catch(() => setErrored(true));
  }, [cached, src]);

  const handleClick = useCallback(() => {
    if (!projectId || !filePath) return;
    const name = filePath.split('/').pop() || filePath;
    dispatch({
      type: 'OPEN_TAB',
      tab: { id: `file:${filePath}`, type: 'file', label: name, filePath, projectId },
    });
  }, [dispatch, projectId, filePath]);

  if (errored) return <ImageError>Image not found</ImageError>;
  if (!dataSrc) return null;

  return (
    <ImageWrap $bgColor={bgColor} {...hoverProps}>
      <StyledImg
        src={dataSrc}
        alt={alt || ''}
        onError={() => setErrored(true)}
        onClick={handleClick}
      />
      {isHovering && <ImageViewerButtons onDownload={handleDownload} onToggleDark={toggleDarkMode} />}
    </ImageWrap>
  );
};

const StyledImg = styled.img`
  display: block;
  max-height: 200px;
  max-width: 100%;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover {
    opacity: 0.85;
  }
`;

const ImageError = styled.span`
  display: inline-block;
  padding: 4px 8px;
  background: #fce8e6;
  color: #c5221f;
  border-radius: 4px;
  font-size: 12px;
`;

const ImageWrap = styled.span<{ $bgColor: string }>`
  display: block;
  margin: 8px 0;
  position: relative;
  width: fit-content;
  border-radius: 6px;
  background-color: ${(p) => p.$bgColor};
  transition: background-color 0.2s ease;
`;
